// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xIsAuthenticated } from "@hardwoods/XDL/Utilities";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { CustomAppState } from "@rugby/App/Store/Reducers";
import { getProductToggleTableMode } from "@rugby/App/Store/Selectors/CustomProductDetails";
import { isTabletDevice } from "@rugby/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState, ownProps: HasParentProductId) => {
    return {
        enableTableMode: xIsAuthenticated(state.context.session) && (getSettingsCollection(state)?.customSettings as any)?.enableTableMode,
        variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
        tableMode: getProductToggleTableMode(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasParentProductId;

interface ProductInfoTableToggleStyles {
    toggleStyles: CheckboxPresentationProps;
}

const productInfoTableToggleStyles: ProductInfoTableToggleStyles = {
    toggleStyles: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: auto;
            height: 100%;
            margin-top: 30px;
        `,
        typographyProps: {
            css: css`
                white-space: nowrap;
                margin-right: 10px;
            `,
        }
    }
};

const styles = productInfoTableToggleStyles;

const ProductInfoTableToggle = ({
    enableTableMode,
    tableMode,
    variantChildren,
}: Props) => {

    const [checked, setChecked] = useState<boolean>(tableMode);
    const dispatch = useDispatch();


    const toggleClickHandler = (value: boolean) => {
        dispatch({
            type: "Data/CustomProductDetails/ProductToggleTableMode",
            tableMode: value,
        });
        setChecked(value);
    };

    useEffect(() => {
        if (enableTableMode && variantChildren?.length && !isTabletDevice()) {
            toggleClickHandler(true);
        }
    }, []);

    if (!enableTableMode || !variantChildren?.length || isTabletDevice()) {
        return null
    }

    return (
        <Checkbox
            variant="toggle"
                checked={checked}
                onChange={(_, value) => toggleClickHandler(value)}
                {...styles.toggleStyles}
            >
                {translate("Table Mode")}
        </Checkbox>
    )
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps)(ProductInfoTableToggle)),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Product Info Table Toggle",
    },
};

export default widgetModule;
